import React, { useRef, useState } from "react";
import { useNavigate } from 'react-router-dom';

function AddBlog() {
  const [formData, setFormData] = useState({
    title: "",
    image: "",
    content: "",
    keywords: "",
    date: "",
    author: "",
    category: ""
  });

  const categories = [
    "Technology",
    "Business",
    "Health",
    "Science",
    "Entertainment",
    "Sports",
    "Travel",
    "Food",
    "Lifestyle",
    "Education"
  ];

  const handleImageChange = (e) => {
    setFormData((prev) => ({
      ...prev,
      image: e.target.files[0],
    }));
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const fileInputRef = useRef(null);
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();

    const form = new FormData();

    const formattedDate = new Date(formData.date).toLocaleDateString("en-IN", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });

    form.append("title", formData.title);
    form.append("content", formData.content);
    form.append("keywords", formData.keywords);
    form.append("date", formattedDate);
    form.append("author", formData.author);
    form.append("category", formData.category);
    
    if (formData.image) {
      form.append("image", formData.image);
    }

    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE}/blogs`, {
        method: "POST",
        body: form,
      });

      const result = await response.json();

      if (response.ok) {
        alert("Blog submitted successfully!");
        setFormData({
          title: "",
          image: "",
          content: "",
          keywords: "",
          date: "",
          author: "",
          category: ""
        });

        if (fileInputRef.current) {
          fileInputRef.current.value = "";
        }
      } else {
        alert(result.error || "Failed to submit blog");
      }
    } catch (err) {
      console.error(err);
      alert("Error submitting blog");
    }
  };

  return (
    <div className="container py-5">
      <div className="row justify-content-center">
        <div className="col-lg-8">
          <div className="card shadow-lg">
            <div className="card-header bg-primary text-white">
              <h2 className="mb-0 text-center">Create New Blog Post</h2>
            </div>
            <div className="card-body p-4">
              <form onSubmit={handleSubmit}>
                <div className="row">
                  {/* Left Column */}
                  <div className="col-md-6">
                    <div className="mb-3">
                      <label htmlFor="title" className="form-label fw-bold">
                        Blog Title*
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        id="title"
                        name="title"
                        value={formData.title}
                        onChange={handleChange}
                        required
                        placeholder="Enter blog title"
                      />
                    </div>

                    <div className="mb-3">
                      <label htmlFor="author" className="form-label fw-bold">
                        Author*
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        id="author"
                        name="author"
                        value={formData.author}
                        onChange={handleChange}
                        required
                        placeholder="Author name"
                      />
                    </div>

                    <div className="mb-3">
                      <label htmlFor="date" className="form-label fw-bold">
                        Publish Date*
                      </label>
                      <input
                        type="date"
                        className="form-control"
                        id="date"
                        name="date"
                        value={formData.date}
                        onChange={handleChange}
                        required
                      />
                    </div>

                    <div className="mb-3">
                      <label htmlFor="category" className="form-label fw-bold">
                        Category*
                      </label>
                      <select
                        className="form-select"
                        id="category"
                        name="category"
                        value={formData.category}
                        onChange={handleChange}
                        required
                      >
                        <option value="">Select a category</option>
                        {categories.map((category, index) => (
                          <option key={index} value={category}>
                            {category}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  {/* Right Column */}
                  <div className="col-md-6">
                    <div className="mb-3">
                      <label htmlFor="image" className="form-label fw-bold">
                        Featured Image
                      </label>
                      <input
                        type="file"
                        className="form-control"
                        id="image"
                        name="image"
                        onChange={handleImageChange}
                        ref={fileInputRef}
                        accept="image/*"
                      />
                      <small className="text-muted">Recommended size: 1200x630px</small>
                    </div>

                    <div className="mb-3">
                      <label htmlFor="keywords" className="form-label fw-bold">
                        Keywords/Tags
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        id="keywords"
                        name="keywords"
                        value={formData.keywords}
                        onChange={handleChange}
                        placeholder="Comma-separated keywords"
                      />
                    </div>

                    <div className="mb-3">
                      <div className="d-flex justify-content-between align-items-center">
                        <label htmlFor="content" className="form-label fw-bold">
                          Content Preview
                        </label>
                        <small className="text-muted">{formData.content.length} characters</small>
                      </div>
                      <div className="card p-3 bg-light" style={{ minHeight: '100px' }}>
                        {formData.content.substring(0, 150)}...
                      </div>
                    </div>
                  </div>
                </div>

                {/* Full width content editor */}
                <div className="mb-4">
                  <label htmlFor="content" className="form-label fw-bold">
                    Blog Content*
                  </label>
                  <textarea
                    className="form-control"
                    id="content"
                    name="content"
                    value={formData.content}
                    onChange={handleChange}
                    rows={10}
                    required
                    placeholder="Write your blog content here..."
                  />
                </div>

                <div className="d-grid gap-2 d-md-flex justify-content-md-end">
                  <button 
                    type="button" 
                    className="btn btn-outline-secondary me-md-2"
                    onClick={() => {
                      setFormData({
                        title: "",
                        image: "",
                        content: "",
                        keywords: "",
                        date: "",
                        author: "",
                        category: ""
                      });
                      if (fileInputRef.current) {
                        fileInputRef.current.value = "";
                      }
                    }}
                  >
                    Clear Form
                  </button>
                  <button type="submit" className="btn btn-primary px-4">
                    Publish Blog
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default AddBlog;